/**
 * The change-set viewer — the answer to "what exactly is about to ship?" (§11.3).
 * It reads the `changes/index.json` a host wrote to the artifacts bucket and
 * renders it two ways (by component, by file), with each file's unified patch
 * fetched on demand from its own storage object. This is the human cross-check
 * before publish/deploy, so it favours completeness (every file, even ones that
 * map to no component) over brevity.
 */
import { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Chip,
    CircularProgress,
    Link,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    changesByComponent,
    type ChangedFile,
    type ChangeSetIndex,
    type ChangeSetPointer,
    type FileComponentAttribution,
} from '@finvari/deployer';
import { useArtifact } from '../hooks';

export interface ChangeSetViewProps {
    pointer: ChangeSetPointer | undefined;
    /**
     * Show only the files attributed to this component, directly or through a shared
     * dependency. The component detail panel's Changes tab: an operator deciding
     * whether to deploy `web` wants that component's diff, not the release's.
     */
    component?: string;
}

export function ChangeSetView({ pointer, component }: ChangeSetViewProps) {
    const { text, error, loading } = useArtifact(pointer?.indexObject);
    const [tab, setTab] = useState(0);

    if (!pointer) return <Alert severity="info">No change set yet — it is computed during capture.</Alert>;
    if (loading) return <CircularProgress size={20} />;
    if (error) return <Alert severity="warning">Could not load the change set: {error}</Alert>;

    let index: ChangeSetIndex;
    try {
        index = JSON.parse(text ?? '') as ChangeSetIndex;
    } catch (e) {
        return <Alert severity="warning">Change set index is unreadable: {String(e)}</Alert>;
    }

    if (component) return <OneComponent index={index} component={component} />;

    return (
        <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
                {index.files.length} files · +{index.totalInsertions} −{index.totalDeletions} · {pointer.baseline} →{' '}
                {pointer.head}
            </Typography>
            <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
                <Tab label="By component" />
                <Tab label="By file" />
            </Tabs>
            {tab === 0 ? <ByComponent index={index} /> : <ByFile files={index.files} />}
        </Stack>
    );
}

/**
 * One component's files, split by how they got there. The shared-dependency files are
 * separated and named: a `web` deploy whose only changes are in `model/` is a
 * different decision from one where somebody edited `web/src`, and lumping them
 * together hides exactly that.
 */
function OneComponent({ index, component }: { index: ChangeSetIndex; component: string }) {
    const files = changesByComponent(index).get(component) ?? [];
    const attribution = (f: ChangedFile) => f.components.find((c) => c.component === component);
    const direct = files.filter((f) => attribution(f)?.kind === 'direct');
    const viaDependency = files.filter((f) => attribution(f)?.kind === 'dependency');
    // A third split inside the shared-dependency files, where the report has an
    // answer: the ones this component's own code can reach at runtime are the
    // reason it redeploys, and the rest are only the reason it rebuilds. Files the
    // report said nothing about (a `saml/` trigger, or a release cut before the
    // analysis existed) count as reachable — the conservative reading.
    const reaching = viaDependency.filter((f) => attribution(f)?.runtime !== false);
    const compileOnly = viaDependency.filter((f) => attribution(f)?.runtime === false);

    if (files.length === 0) {
        return <Alert severity="info">This release changes nothing in {component}.</Alert>;
    }

    return (
        <Stack spacing={1}>
            {direct.length > 0 && (
                <>
                    <Typography variant="subtitle2">{direct.length} files in {component}</Typography>
                    <ByFile files={direct} />
                </>
            )}
            {reaching.length > 0 && (
                <>
                    <Typography variant="subtitle2">
                        {reaching.length} files in shared dependencies it builds against
                        {compileOnly.length > 0 ? ' and can reach at runtime' : ''}
                    </Typography>
                    <ByFile files={reaching} />
                </>
            )}
            {compileOnly.length > 0 && (
                <>
                    <Typography variant="subtitle2">
                        {compileOnly.length} files it rebuilds against but cannot reach at runtime
                    </Typography>
                    <ByFile files={compileOnly} />
                </>
            )}
        </Stack>
    );
}

function ByComponent({ index }: { index: ChangeSetIndex }) {
    const grouped = changesByComponent(index);
    const entries = [...grouped.entries()].sort((a, b) => (a[0] ?? '~').localeCompare(b[0] ?? '~'));
    return (
        <Box>
            {entries.map(([component, files]) => (
                <Accordion key={component ?? '(no component)'} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ flexGrow: 1 }}>{component ?? '(no component)'}</Typography>
                        <Chip size="small" label={`${files.length} files`} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <ByFile files={files} />
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
}

function ByFile({ files }: { files: ChangedFile[] }) {
    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell />
                    <TableCell>File</TableCell>
                    <TableCell align="right">+/−</TableCell>
                    <TableCell>Components</TableCell>
                    <TableCell>PRs</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {files.map((f) => (
                    <FileRow key={f.path} file={f} />
                ))}
            </TableBody>
        </Table>
    );
}

function FileRow({ file }: { file: ChangedFile }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <TableRow hover>
                <TableCell>
                    <Chip size="small" label={file.status} />
                </TableCell>
                <TableCell>
                    {file.patchObject ? (
                        <Link component="button" type="button" onClick={() => setOpen((o) => !o)} sx={{ textAlign: 'left' }}>
                            {file.path}
                        </Link>
                    ) : (
                        file.path
                    )}
                    {file.previousPath && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            was {file.previousPath}
                        </Typography>
                    )}
                </TableCell>
                <TableCell align="right">
                    <Typography variant="caption" color="success.main" component="span">
                        +{file.insertions}
                    </Typography>{' '}
                    <Typography variant="caption" color="error.main" component="span">
                        −{file.deletions}
                    </Typography>
                </TableCell>
                <TableCell>
                    {/*
                      * A Pulumi file attributes to no component on purpose, and an empty
                      * cell would read as "nothing ships this" — the opposite of the
                      * truth. The chip says what it belongs to and that no deploy here
                      * touches it.
                      */}
                    {file.infraProgram && (
                        <Tooltip
                            title={`Part of the ${file.infraProgram} Pulumi program. No component deploy applies it — an owner runs \`pulumi up\` there.`}
                        >
                            <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5 }}
                                label={file.infraProgram}
                            />
                        </Tooltip>
                    )}
                    {file.components.map((c) => (
                        <Tooltip key={`${c.component}:${c.kind}`} title={chipTooltip(c)}>
                            <Chip
                                size="small"
                                variant={c.kind === 'direct' ? 'filled' : 'outlined'}
                                // Struck through rather than hidden: the component is
                                // still in the release and still compiles this file, and
                                // dropping the chip would make the by-file view disagree
                                // with the component table about why.
                                sx={{
                                    mr: 0.5,
                                    mb: 0.5,
                                    ...(c.runtime === false ? { textDecoration: 'line-through', opacity: 0.6 } : {}),
                                }}
                                label={c.kind === 'direct' ? c.component : `${c.component} (via ${c.via})`}
                            />
                        </Tooltip>
                    ))}
                </TableCell>
                <TableCell>{file.prNumbers.map((n) => `#${n}`).join(' ')}</TableCell>
            </TableRow>
            {open && file.patchObject && (
                <TableRow>
                    <TableCell colSpan={5} sx={{ p: 0 }}>
                        <PatchView object={file.patchObject} />
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}

/** What a component chip on a file row means, spelled out. */
function chipTooltip(c: FileComponentAttribution): string {
    if (c.kind === 'direct') return `${c.component} owns this file`;
    if (c.runtime === false) {
        return `${c.component} recompiles against ${c.via}, but nothing it ships imports this file at runtime`;
    }
    return c.reason ?? `${c.component} builds against ${c.via}`;
}

function PatchView({ object }: { object: string }) {
    const { text, error, loading } = useArtifact(object);
    if (loading) return <CircularProgress size={16} sx={{ m: 1 }} />;
    if (error) return <Alert severity="warning">Could not load patch: {error}</Alert>;
    return (
        <Box
            component="pre"
            sx={{ m: 0, p: 1, fontSize: 12, overflowX: 'auto', bgcolor: 'grey.900', color: 'grey.100' }}
        >
            {text}
        </Box>
    );
}
